<template>
  <br-component component="blog">
    <template #default="{ page, component }">
      <BlogTopicsHeader :page="page" :component="component" />
    </template>
  </br-component>
  <div class="mb-lg mt-xs text-charcoal-default" data-test-id="blogDetailsContainer">
    <br-component component="main/container">
      <template #default="{ page }">
        <div class="grid grid-cols-1 sm:grid-cols-5 items-start mb-md sm:mb-xl">
          <NuxtImg
            :src="getSrcOf(post(page).image, page)"
            :alt="post(page).title"
            format="avif,webp,jpeg"
            fetchpriority="high"
            class="w-full h-[550px] object-cover col-span-3"
            data-test-id="branchDetailsMarketingBanner"
          />

          <Stack
            direction="col"
            justify="center"
            class="col-span-2 bg-mkm-blue-default text-white h-full p-sm sm:pl-2xl gap-sm"
          >
            <Heading size="xl">
              {{ post(page).title }}
            </Heading>

            <Heading size="xs"> Published on {{ formatArticleDate(post(page).date) }} </Heading>
          </Stack>
        </div>
        <div class="max-w-[1240px] mx-auto grid sm:grid-cols-12 px-xs sm:md-none" data-test-id="blogDetailsContent">
          <div class="min-w-[300px] mb-sm sm:col-span-3" data-test-id="blogDetailsAside">
            <Text size="lg" weight="semi">
              Posted in <span class="text-mkm-blue-light"> {{ post(page).category.selectionValues[0].label }}</span>
            </Text>

            <Text size="lg" weight="semi" class="mt-2xs sm:mt-xs">
              by <span class="text-mkm-blue-light"> {{ post(page).author }} </span>
            </Text>

            <Stack align="center" gap="2xs" class="mt-sm sm:mt-md" v-if="post(page).readingTime">
              <Icon name="time-outline" :size="16" />
              <Text size="sm" weight="semi"> {{ post(page).readingTime }} min read</Text>
            </Stack>
          </div>
          <div v-html="post(page).body.value" class="col-span-9 rich-text" />
        </div>

        <LazyRelatedProducts
          v-if="products.length > 0"
          :products="{ items: products }"
          title="Products in this article"
        />

        <!-- Next Article -->

        <Heading size="sm" class="sm:hidden mt-lg px-xs text-mkm-blue-default uppercase" v-if="post(page).nextArticle">
          Up Next
        </Heading>

        <div
          v-if="post(page).nextArticle"
          data-test-id="blogDetailsUpNext"
          :class="[
            'my-xs sm:my-xl relative md:px-lg lg:px-none',
            'before:absolute before:bg-grey-lighter before:h-[100%] before:sm:h-[87.5%] before:left-none before:top-none before:w-[100%] before:z-[-1]',
          ]"
        >
          <Stack
            class="w-full md:max-w-[1200px] mx-auto"
            justify="between"
            gap="none"
            direction="col"
            :breakpoints="{
              direction: {
                sm: 'row',
              },
              gap: {
                sm: 'md',
                md: 'xl',
                lg: '2xl',
              },
              justify: {
                sm: 'center',
              },
            }"
          >
            <NuxtPicture
              :src="getSrcOf(getPostContent(post(page).nextArticle, page).image, page)"
              :alt="getPostContent(post(page).nextArticle, page).displayName"
              :img-attrs="{ class: 'w-full sm:w-[50%] max-h-[375px] object-cover' }"
              data-test-id="UpNextImage"
            />

            <section class="p-xs sm:p-none sm:my-2xl">
              <Heading size="xs" class="hidden sm:block tracking-[-0.015em] uppercase"> Up Next </Heading>

              <Heading size="xxs" class="text-grey-darker uppercase sm:hidden">
                {{ getPostContent(post(page).nextArticle, page).category.selectionValues[0].label }}
              </Heading>

              <Heading size="md" class="my-2xs sm:mb-xs sm:mt-sm tracking-[-0.015em]" data-test-id="UpNextName">
                {{ getPostContent(post(page).nextArticle, page).displayName }}
              </Heading>

              <Heading size="xxs" class="text-grey-darker uppercase hidden sm:block tracking-[-0.015em]">
                {{ getPostContent(post(page).nextArticle, page).category.selectionValues[0].label }}
              </Heading>
            </section>
          </Stack>
        </div>

        <!-- Similar Articles -->
        <div class="my-2xl ml-xs" v-if="post(page).relatedArticles.length > 0">
          <Heading size="md" class="mb-md text-center"> Similar Articles </Heading>
          <LazySlider
            :space-between="24"
            :draggable="true"
            :autoplay="false"
            :class="['my-md mx-auto', post(page).relatedArticles.length <= 3 && 'lg:w-4/5']"
            :breakpoints="breakpoints(post(page))[0]"
            :items="post(page).relatedArticles"
          >
            <swiper-slide v-for="(article, index) in getRelatedArticles(post(page), page)" :key="index">
              <BlogPost
                :article="{
                  title: article.displayName,
                  category: article.category,
                  image: getSrcOf(article.image, page),
                  date: formatArticleDate(article.date),
                  link: getArticleLink(article),
                  meta_description: article.meta_description,
                  readingTime: article.readingTime,
                  linkComponent: NuxtLink,
                }"
                direction="col"
              />
            </swiper-slide>
          </LazySlider>
        </div>
      </template>
    </br-component>
  </div>
</template>

<script setup lang="ts">
import { Page } from "@bloomreach/spa-sdk";
import { SwiperSlide } from "swiper/vue";
import { formatArticleDate, getArticleLink } from "@/composables/UseContent/useArticleList";

const idx = ref(0);
const NuxtLink = resolveComponent("NuxtLink");
const productIds = ref<string[]>([]);
const products = ref<any[]>([]);
const { fetchProductDetails } = useSearchAndMerch();

const post = (page: Page) => {
  const post = (page.getDocument() as any)?.getData();
  productIds.value = post.relatedItems.map((item: any) => item.productid.slice(3, 10));
  return post;
};
const getSrcOf = (img: any, p: Page) => (p.getContent(img.$ref) as any)?.getOriginal()?.getUrl() || "";

const getPostContent = (post: any, page: any) => {
  return page.getContent(post.$ref).getData();
};

const getRelatedArticles = (post: any, page: any) => {
  return post?.relatedArticles?.map((post: any) => getPostContent(post, page)) || [];
};

const breakpoints = (post: any) => {
  return [
    {
      320: {
        slidesPerView: 1.2,
      },
      520: {
        slidesPerView: 1.4,
      },
      768: {
        slidesPerView: 2.2,
      },
      960: {
        slidesPerView: 3.1,
      },
      1024: {
        slidesPerView: post.relatedArticles.length,
      },
    },
  ];
};

onMounted(() => {
  if (productIds.value.length > 0)
    fetchProductDetails(productIds.value[idx.value])
      .then((data) => products.value.push(data))
      .catch((error) => {
        console.error("Error fetching first product details:", error);
      });
});

watch(products.value, () => {
  if (++idx.value < productIds.value.length) {
    fetchProductDetails(productIds.value[idx.value])
      .then((data) => products.value.push(data))
      .catch((error) => {
        console.error("Error fetching product details:", error);
      });
  }
});
</script>

<style scoped>
.next-article::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  height: 90%;
  background: #f8f8f8;
}
</style>
